import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, forkJoin, map, of, switchMap, tap } from 'rxjs';
import {
  BackendQuestionMode,
  BackendQuestionSelection,
  BackendShowLobbyDetail,
  BackendShowLobbyQuestion,
  BackendShowStatus,
  JoinShowLobbyResponse,
  PreparedShow,
  PreparedShowQuestion,
  PreparedShowStatus,
  QuestionUsageMode,
  ShowFormatOption,
  UpsertShowLobbyRequest
} from '../models/show-lobby.model';

import { appEnvironment } from '../app-environment';

@Injectable({ providedIn: 'root' })
export class ShowLobbyService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${appEnvironment.apiBaseUrl}/show-lobbies`;

  readonly formats: ShowFormatOption[] = [
    {
      id: 'quizpoker',
      title: 'Quizpoker',
      questionPoolId: 'quizpoker',
      categories: ['Allgemeinwissen', 'Gaming', 'Internet & Creator', 'Technik', 'Film & Serien'],
      difficulties: ['easy', 'medium', 'hard']
    },
    {
      id: 'schaetzduell',
      title: 'Schätzduell',
      questionPoolId: 'schaetzduell',
      categories: ['Allgemeinwissen', 'Gaming', 'Sport', 'Geografie'],
      difficulties: ['easy', 'medium', 'hard']
    },
    {
      id: 'begriffsarena',
      title: 'Begriffsarena',
      questionPoolId: 'begriffsarena',
      categories: ['Gaming', 'Alltag', 'Popkultur', 'Technik'],
      difficulties: ['easy', 'medium', 'hard']
    },
    {
      id: 'lockout_bingo',
      title: 'Lockout Bingo',
      questionPoolId: 'lockout_bingo',
      categories: ['Gaming', 'Challenges', 'Speedrun', 'Community'],
      difficulties: ['easy', 'medium', 'hard']
    }
  ];

  private readonly showsSignal = signal<PreparedShow[]>([]);
  readonly shows = this.showsSignal.asReadonly();

  loadShows(): Observable<PreparedShow[]> {
    return this.http.get<BackendShowLobbyDetail[]>(this.apiUrl).pipe(
      switchMap(summaries => {
        if (!summaries.length) {
          return of([]);
        }

        return forkJoin(summaries.map(summary => this.getShow(String(summary.id))));
      }),
      tap(shows => this.showsSignal.set(shows))
    );
  }

  getShow(showId: string): Observable<PreparedShow> {
    return this.http.get<BackendShowLobbyDetail>(`${this.apiUrl}/${showId}`).pipe(
      map(show => this.mapShow(show))
    );
  }

  saveDraft(show: Partial<PreparedShow>): Observable<PreparedShow> {
    const request = this.toUpsertRequest(show);
    const request$ = show.id
      ? this.http.put<BackendShowLobbyDetail>(`${this.apiUrl}/${show.id}`, request)
      : this.http.post<BackendShowLobbyDetail>(this.apiUrl, request);

    return request$.pipe(
      map(saved => this.mapShow(saved)),
      tap(saved => this.upsertLocalShow(saved))
    );
  }

  saveAndActivate(show: Partial<PreparedShow>): Observable<PreparedShow> {
    const request = this.toUpsertRequest(show);
    const params = new HttpParams().set('activate', 'true');
    const request$ = show.id
      ? this.http.put<BackendShowLobbyDetail>(`${this.apiUrl}/${show.id}`, request, { params })
      : this.http.post<BackendShowLobbyDetail>(this.apiUrl, request, { params });

    return request$.pipe(
      map(saved => this.mapShow(saved)),
      tap(saved => this.upsertLocalShow(saved))
    );
  }

  activateShow(showId: string): Observable<PreparedShow> {
    return this.http.post<BackendShowLobbyDetail>(`${this.apiUrl}/${showId}/activate`, {}).pipe(
      map(show => this.mapShow(show)),
      tap(show => this.upsertLocalShow(show))
    );
  }

  finishShow(showId: string): Observable<PreparedShow> {
    return this.http.post<BackendShowLobbyDetail>(`${this.apiUrl}/${showId}/end`, {}).pipe(
      map(show => this.mapShow(show)),
      tap(show => this.upsertLocalShow(show))
    );
  }

  joinByCode(code: string, displayName: string): Observable<PreparedShow> {
    return this.http.post<JoinShowLobbyResponse>(`${this.apiUrl}/join`, {
      lobbyCode: code,
      displayName
    }).pipe(
      map(response => this.mapShow(response.show))
    );
  }

  previewQuestions(show: Partial<PreparedShow>): Observable<PreparedShowQuestion[]> {
    return this.http.post<BackendShowLobbyQuestion[]>(`${this.apiUrl}/question-preview`, {
      format: show.format ?? this.formats[0].id,
      questionSelection: this.toQuestionSelection(show)
    }).pipe(
      map(questions => questions.map((question, index) => this.mapQuestion(question, index)))
    );
  }

  getFormat(formatId: string): ShowFormatOption {
    return this.formats.find(format => format.id === formatId) ?? this.formats[0];
  }

  getPoolIdForFormat(formatId: string): string {
    return this.getFormat(formatId).questionPoolId;
  }

  private upsertLocalShow(show: PreparedShow): void {
    const current = this.showsSignal();
    const exists = current.some(item => item.id === show.id);
    const updated = exists
      ? current.map(item => item.id === show.id ? show : item)
      : [show, ...current];

    this.showsSignal.set(updated);
  }

  private mapShow(show: BackendShowLobbyDetail): PreparedShow {
    return {
      id: String(show.id),
      title: show.title,
      format: show.format,
      date: show.scheduledDate ?? new Date().toISOString().slice(0, 10),
      host: show.hostName || 'DaniiPlace',
      status: this.mapStatusFromBackend(show.status),
      lobbyCode: show.status === 'active' ? show.lobbyCode ?? null : null,
      notes: show.notes ?? '',
      selectedCategories: show.questionSelection?.categories ?? [],
      selectedDifficulties: show.questionSelection?.difficulties ?? [],
      questionUsageMode: this.mapQuestionModeFromBackend(show.questionSelection?.questionMode ?? (show.usesCustomQuestions ? 'custom' : 'unused_only')),
      playedQuestionPercentage: show.questionSelection?.playedQuestionPercentage ?? 0,
      playedCorrectnessTarget: show.questionSelection?.correctnessTargetPercentage ?? 50,
      questionLimitMode: show.questionSelection?.isUnlimited || show.isUnlimited ? 'unlimited' : 'limited',
      questionLimitCount: show.questionSelection?.questionLimit ?? show.questionLimit ?? null,
      selectedQuestions: (show.questions ?? []).map((question, index) => this.mapQuestion(question, index)),
      createdAt: show.createdAt,
      updatedAt: show.endedAt ?? show.activatedAt ?? show.createdAt
    };
  }

  private mapQuestion(question: BackendShowLobbyQuestion, index: number): PreparedShowQuestion {
    const data = question.data ?? {};

    return {
      id: question.id ? String(question.id) : `${question.poolId}_${question.sourceQuestionId ?? index}`,
      sourceQuestionId: question.sourceQuestionId ?? null,
      poolId: question.poolId,
      questionText: question.question,
      solutionText: question.solution ?? this.stringFromData(data, 'solution_text') ?? this.stringFromData(data, 'solution_value'),
      category: this.stringFromData(data, 'category') ?? (question.isCustom ? 'Eigene Frage' : 'Allgemeinwissen'),
      difficulty: this.stringFromData(data, 'difficulty') ?? (question.isCustom ? 'custom' : 'medium'),
      alreadyPlayed: this.booleanFromData(data, 'is_played'),
      wasAnsweredCorrectly: this.nullableBooleanFromData(data, 'answered_correctly'),
      hitRatePercent: this.numberFromData(data, 'answer_accuracy_percent'),
      isCustom: question.isCustom,
      data
    };
  }

  private toUpsertRequest(show: Partial<PreparedShow>): UpsertShowLobbyRequest {
    return {
      title: show.title?.trim() || 'Unbenannte Show',
      format: show.format ?? this.formats[0].id,
      scheduledDate: show.date ?? null,
      hostName: show.host?.trim() || 'DaniiPlace',
      notes: show.notes?.trim() || '',
      questionSelection: this.toQuestionSelection(show),
      questions: (show.selectedQuestions ?? []).map((question, index) => this.toBackendQuestion(question, show.format ?? this.formats[0].id, index))
    };
  }

  private toQuestionSelection(show: Partial<PreparedShow>): BackendQuestionSelection {
    return {
      categories: this.cleanSelection(show.selectedCategories ?? []),
      difficulties: this.cleanSelection(show.selectedDifficulties ?? []),
      questionMode: this.mapQuestionModeToBackend(show.questionUsageMode ?? 'unused_only'),
      playedQuestionPercentage: this.clampPercent(show.playedQuestionPercentage ?? 0),
      correctnessTargetPercentage: this.clampPercent(show.playedCorrectnessTarget ?? 50),
      isUnlimited: show.questionLimitMode === 'unlimited',
      questionLimit: show.questionLimitMode === 'unlimited' ? null : Math.max(1, Number(show.questionLimitCount ?? 50))
    };
  }

  private toBackendQuestion(question: PreparedShowQuestion, formatId: string, index: number): BackendShowLobbyQuestion {
    return {
      sourceQuestionId: question.sourceQuestionId ? Number(question.sourceQuestionId) : null,
      poolId: question.poolId || this.getPoolIdForFormat(formatId),
      isCustom: question.isCustom,
      sortOrder: index + 1,
      question: question.questionText,
      solution: question.solutionText ?? null,
      data: {
        ...(question.data ?? {}),
        category: question.category,
        difficulty: question.difficulty,
        is_played: question.alreadyPlayed,
        answered_correctly: question.wasAnsweredCorrectly,
        answer_accuracy_percent: question.hitRatePercent
      }
    };
  }

  private mapStatusFromBackend(status: BackendShowStatus | string): PreparedShowStatus {
    return status === 'ended' ? 'finished' : status as PreparedShowStatus;
  }

  private mapQuestionModeFromBackend(mode: BackendQuestionMode | string): QuestionUsageMode {
    if (mode === 'allow_played') return 'include_played';
    if (mode === 'custom') return 'custom_questions';
    return 'unused_only';
  }

  private mapQuestionModeToBackend(mode: QuestionUsageMode): BackendQuestionMode {
    if (mode === 'include_played') return 'allow_played';
    if (mode === 'custom_questions') return 'custom';
    return 'unused_only';
  }

  private cleanSelection(values: string[]): string[] {
    return Array.from(new Set(values.filter(value => value && value !== 'all')));
  }

  private clampPercent(value: number): number {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return 0;
    return Math.min(100, Math.max(0, numeric));
  }

  private stringFromData(data: Record<string, unknown>, key: string): string | null {
    const value = data[key];
    return value === null || value === undefined ? null : String(value);
  }

  private numberFromData(data: Record<string, unknown>, key: string): number | null {
    const value = data[key];
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    return Number.isNaN(numeric) ? null : numeric;
  }

  private booleanFromData(data: Record<string, unknown>, key: string): boolean {
    const value = data[key];
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private nullableBooleanFromData(data: Record<string, unknown>, key: string): boolean | null {
    const value = data[key];
    if (value === null || value === undefined || value === '') return null;
    return value === true || value === 1 || value === '1' || value === 'true';
  }
}
